import { Fragment, ReactElement } from "react";
import TextHighlighterInterface from "./TextHighlighter.types";

export type RegexCacheType = Record<string, any>;
export const regexCache: RegexCacheType = {
  /*  searchText1: {
    readCount: 1,
    lastAccessed: Date.now(),
    ignoreChars1: {
      result?: "result11",
      resultExactMatch?: "result11",
    }...
  */
};

export const moduleState = { numCacheEntries: 0 };
const specialCharacters = "-/\\^$*+?.()|[]{}".split("");

export const buildEscapeString = (s: string = "") => {
  return s.split("").reduce<string>((acc, c) => {
    if (specialCharacters.includes(c)) {
      return `${acc}\\${c}`;
    }
    return `${acc}${c}`;
  }, "");
};

//Construct a case insensitive regex pattern
export const buildRegexI = (
  searchText: string = "",
  ignoreChars: string = "",
  exactMatch: boolean = false
): RegExp => {
  if (!searchText || !searchText.length) {
    return new RegExp("");
  }

  //+++ check the cache +++
  const cachedValue = [
    searchText,
    ignoreChars,
    exactMatch ? "resultExactMatch" : "result",
  ].reduce((acc: any, part) => acc && acc[part], regexCache);
  if (cachedValue) {
    regexCache[searchText].readCount++;
    regexCache[searchText].lastAccessed = Date.now();
    return cachedValue;
  }
  //--- check the cache ---

  const ignoreCharsEscaped = buildEscapeString(ignoreChars);
  const regexString = searchText.split("").reduce<string>((acc, c) => {
    if (specialCharacters.includes(c)) {
      if (ignoreChars?.split("").includes(c)) {
        return acc;
      } else {
        return ignoreChars
          ? `${acc}\\${c}[${ignoreCharsEscaped}]*`
          : `${acc}\\${c}`;
      }
    }
    if (ignoreChars?.split("").includes(c)) {
      return acc;
    }
    return ignoreChars ? `${acc}${c}[${ignoreCharsEscaped}]*` : `${acc}${c}`;
  }, "");

  const returnValue = new RegExp(
    exactMatch ? `^${regexString}$` : regexString,
    "i"
  );

  // +++ cache the computed result +++
  if (!regexCache[searchText]) {
    regexCache[searchText] = { readCount: 0 };
  }
  if (!regexCache[searchText][ignoreChars]) {
    regexCache[searchText][ignoreChars] = {};
  }
  if (exactMatch) {
    if (!regexCache[searchText][ignoreChars].resultExactMatch) {
      regexCache[searchText][ignoreChars].resultExactMatch = returnValue;
    }
  } else {
    if (!regexCache[searchText][ignoreChars].result) {
      regexCache[searchText][ignoreChars].result = returnValue;
    }
  }
  // --- cache the computed result

  // +++ purge cache if necessessary +++
  if (++moduleState.numCacheEntries > 10000) {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    Object.keys(regexCache).forEach((key, index) => {
      if (regexCache[key].lastAccessed < twentyFourHoursAgo) {
        // console.log("Purging regexCache[%o]=%o", key, regexCache[key]);
        delete regexCache[key];
      }
    });
    moduleState.numCacheEntries = Object.keys(regexCache).length;
  }
  // --- purge cache if necessessary ---

  return returnValue || "";
};

const TextHighlighter = ({
  bodyText,
  searchText,
  ignoreChars,
}: TextHighlighterInterface): ReactElement => {
  if (bodyText === null || bodyText === undefined) {
    return <Fragment></Fragment>;
  }

  const bodyTextString = `${bodyText}`; //protect against poor data quality, in case an object is passed.

  if (!searchText || (searchText && typeof searchText !== "string")) {
    return <Fragment>{bodyTextString}</Fragment>;
  }

  const searchTextRegex = buildRegexI(`${searchText}`, ignoreChars);
  const match = bodyTextString.match(searchTextRegex);
  if (match && match.index !== undefined && match.index > -1 && match[0]) {
    const foundIdx = match.index;
    const leftPart = bodyTextString.substring(0, foundIdx);
    const matchedPart = match[0];
    const rightPart = bodyTextString.substring(foundIdx + matchedPart.length);

    return (
      <Fragment>
        {leftPart}
        <span className="matched-text">{matchedPart}</span>
        {rightPart && (
          <TextHighlighter
            bodyText={rightPart}
            searchText={searchText}
            ignoreChars={ignoreChars}
          />
        )}
      </Fragment>
    );
  } else {
    return <Fragment>{bodyTextString}</Fragment>;
  }
};

export default TextHighlighter;
